<template>
	<div class="news-trending">
		<div class="news-trending__wrapper">
			<div class="news-trending__header">
				<h2 class="news-trending__title">{{ title }}</h2>

				<NewsSwitcher
					class="news-trending__time-range"
					v-model="filters.timeFrame"
					:select-items="$helpers.timeFrames"
				/>
			</div>

			<div class="news-trending__empty" v-if="isLoading">
				<LoaderQuad/>
			</div>

			<div class="news-trending__empty" v-else-if="isNoResultsFound">
				No Results Found
			</div>

			<template v-else>
				<div
					v-if="isScreenWidthLessThen992"
					class="news-trending__body"
				>
					<template v-for="(item, serviceType) in trendingList[filters.timeFrame]">
						<NewsTrendingItem
							v-if="item.length"
							:key="filters.timeFrame + serviceType"
							:id="serviceType"
							:time-frame="nameTimeFrame"
							:item="item"
							:type="type"
						/>
					</template>
				</div>
				<div
					v-else
					class="news-trending__body"
				>
					<div
						v-if="evenTrendingList"
						class="news-trending__body__column"
					>
						<NewsTrendingItem
							v-for="(item, serviceType) in evenTrendingList"
							:key="filters.timeFrame + serviceType"
							:id="serviceType"
							:time-frame="nameTimeFrame"
							:item="item"
							:type="type"
						/>
					</div>
					<div
						v-if="oddTrendingList"
						class="news-trending__body__column"
					>
						<NewsTrendingItem
							v-for="(item, serviceType) in oddTrendingList"
							:key="filters.timeFrame + serviceType"
							:id="serviceType"
							:time-frame="nameTimeFrame"
							:item="item"
							:type="type"
						/>
					</div>
				</div>
			</template>
		</div>
	</div>
</template>

<script>
import {useApi} from '~/composables/api';
import {useNews} from '~/stores/news';

export default {
	name: 'NewsTrending',
	props: {
		title: {
			type: String,
			default: 'Trending'
		},
		tagId: {
			type: Number,
			default: null
		},
		type: {
			type: String,
			default: 'tag'
		}
	},
	async setup() {
		const news = useNews();

		return {
			news
		}
	},
	data() {
		return {
			isLoading: false,
			trendingList: {},
			selectedTrendingList: null,
			lastFilters: null,
			filters: {
				timeFrame: '1d'
			},
			windowWidth: 0
		}
	},
	computed: {
		nameTimeFrame() {
			return this.$helpers.timeFrames.find(item => item.id === this.filters.timeFrame).name;
		},
		isScreenWidthLessThen992() {
			return this.windowWidth < 992;
		},
		onlyFilledTrendingList() {
			if (typeof this.trendingList[this.filters.timeFrame] !== 'undefined') {
				return Object.keys(this.trendingList[this.filters.timeFrame]).reduce((obj, key) => {
					return this.trendingList[this.filters.timeFrame][key].length ? { ...obj, [key]: this.trendingList[this.filters.timeFrame][key] } : obj
				}, {});
			}
			return false;
		},
		evenTrendingList() {
			if (this.onlyFilledTrendingList) {
				const result = Object.keys(this.onlyFilledTrendingList).reduce((obj, key, index) => {
					return index % 2 === 0 ? { ...obj, [key]: this.onlyFilledTrendingList[key] } : obj
				}, {});
				return Object.keys(result).length ? result : false;
			}
			return false;
		},
		oddTrendingList() {
			if (this.onlyFilledTrendingList) {
				const result = Object.keys(this.onlyFilledTrendingList).reduce((obj, key, index) => {
					return index % 2 !== 0 ? { ...obj, [key]: this.onlyFilledTrendingList[key] } : obj
				}, {});
				return Object.keys(result).length ? result : false;
			}
			return false;
		},
		isNoResultsFound() {
			return !this.evenTrendingList && !this.oddTrendingList;
		}
	},
	watch: {
		'news.filters': {
			async handler(newVal, oldVal) {
				// console.log('%cNewsTrending watch() news.filters', 'color:red')
				// console.log('new', JSON.stringify(newVal))
				// console.log('old', JSON.stringify(oldVal))

				if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
					this.trendingList = {};
					this.lastFilters = null;

					await this.fetchTrending();
				}
			},
			deep: true
		},
		'filters.timeFrame'() {
			// console.log('%cwatch() NewsTrending timeFrame', 'color:red')
			this.fetchTrending();
		}
	},
	mounted() {
		// console.log('mounted NewsTrending')
		this.fetchTrending();

		this.handleResize();
		window.addEventListener('resize', this.handleResize);
	},
	unmounted() {
		window.removeEventListener('resize', this.handleResize);
	},
	methods: {
		handleResize() {
			this.windowWidth = window.innerWidth;
		},
		async fetchTrending() {
			// console.log('fetchTrending()', this.trendingList)
			const filters = {timeFrame: this.filters.timeFrame, ...this.news.filters};

			if (this.trendingList[this.filters.timeFrame]) {
				return;
			}

			if (this.lastFilters === JSON.stringify(filters)) {
				return;
			}
			this.isLoading = true;

			const api = useApi();
			let response = null;

			try {
				if (this.type === 'tag') {
					response = await api.news.fetchStatsTags(filters);
				} else {
					response = await api.news.fetchStatsSources({timeFrame: this.filters.timeFrame, tag_id: this.tagId});
				}

				if (response?.stats) {
					this.lastFilters = JSON.stringify(filters);
					this.trendingList[this.filters.timeFrame] = response.stats[this.filters.timeFrame];
				}
			} finally {
				this.isLoading = false;
			}
		}

	}
}
</script>

<style lang="scss" scoped>
.news-trending {
	&__header {
		display: flex;
		justify-content: space-between;
		align-items: baseline;
		margin-top: 5px;
		margin-bottom: 10px;
		border-bottom: 8px solid #344859;
		padding-bottom: 8px;

		@media (max-width: 600px) {
			flex-direction: column;
			gap: 20px;
		}
	}

	&__title {
		font-size: 24px;
		font-weight: 600;
		color: #fff;
		margin-right: 20px;
	}

	&__time-range {
		margin-left: auto;
	}

	&__link {
		font-size: 16px;
		line-height: 30px;
		opacity: 0.5;
		color: #fff;
		margin-left: auto;
		transition: all 0.3s ease-in-out;

		&:hover {
			opacity: 1;
			color: #fff;
		}
	}

	&__body {
		padding-top: 10px;
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		grid-gap: 15px;
		@media (max-width: 992px) {
			grid-template-columns: 1fr;
		}
	}

	&__empty {
		display: flex;
		justify-content: center;
		align-items: center;
		min-height: 30vh;
		padding-bottom: 50px;
		color: $neutrals3;
		font-size: 16px;
	}
}
</style>
